import * as React from "react"
import { CalendarIcon, ClockIcon } from "@radix-ui/react-icons"
import { format } from "date-fns"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import { Input } from "@/components/ui/input"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { Label } from "@radix-ui/react-dropdown-menu"
import { AlertCircle } from "lucide-react"
import { Alert, AlertTitle } from "./alert"

// Warsaw timezone utilities
const WARSAW_TIMEZONE = 'Europe/Warsaw';

// Custom 24-hour time input component
const CustomTimeInput = ({ value, onChange, className, disabled, ...props }) => {
  const [hours, setHours] = React.useState('');
  const [minutes, setMinutes] = React.useState('');
  const [focusedField, setFocusedField] = React.useState(null);
  
  // Parse the incoming value (HH:MM format)
  React.useEffect(() => {
    if (value && value.includes(':')) {
      const [h, m] = value.split(':');
      setHours(h);
      setMinutes(m);
    } else if (!value) {
      setHours('');
      setMinutes('');
    }
  }, [value]);
  
  // Combine hours and minutes and notify parent
  const updateTime = (newHours, newMinutes) => {
    // Use defaults for empty/incomplete values
    const hourStr = newHours || hours || '00';
    const minuteStr = newMinutes || minutes || '00';
    
    // Only update if we have at least one complete field
    if (hourStr.length === 2 || minuteStr.length === 2) {
      const h = parseInt(hourStr.padStart(2, '0'), 10);
      const m = parseInt(minuteStr.padStart(2, '0'), 10);
      
      if (h >= 0 && h <= 23 && m >= 0 && m <= 59) {
        const formattedHours = h.toString().padStart(2, '0');
        const formattedMinutes = m.toString().padStart(2, '0');
        onChange(`${formattedHours}:${formattedMinutes}`);
      }
    }
  };
  
  const handleHoursChange = (e) => {
    const val = e.target.value.replace(/\D/g, ''); // Only allow digits
    
    if (val === '') {
      setHours('');
      updateTime('', minutes);
      return;
    }
    
    // Allow typing up to 2 digits
    if (val.length <= 2) {
      // Only check range if we have 2 digits
      if (val.length === 2) {
        const num = parseInt(val, 10);
        if (num > 23) {
          setHours('23');
          updateTime('23', minutes);
        } else {
          setHours(val);
          updateTime(val, minutes);
          // Auto-focus minutes field
          setTimeout(() => {
            document.getElementById('time-minutes')?.focus();
          }, 10);
        }
      } else {
        // For single digit, just set it without validation
        setHours(val);
      }
    }
  };
  
  const handleMinutesChange = (e) => {
    const val = e.target.value.replace(/\D/g, ''); // Only allow digits
    
    if (val === '') {
      setMinutes('');
      updateTime(hours, '');
      return;
    }
    
    // Allow typing up to 2 digits
    if (val.length <= 2) {
      // Only check range if we have 2 digits
      if (val.length === 2) {
        const num = parseInt(val, 10);
        if (num > 59) {
          setMinutes('59');
          updateTime(hours, '59');
        } else {
          setMinutes(val);
          updateTime(hours, val);
        }
      } else {
        // For single digit, just set it without validation
        setMinutes(val);
      }
    }
  };
  
  const incrementHours = () => {
    const h = parseInt(hours || '0', 10);
    const newHours = h >= 23 ? '00' : String(h + 1).padStart(2, '0');
    setHours(newHours);
    updateTime(newHours, minutes || '00');
  };
  
  const decrementHours = () => {
    const h = parseInt(hours || '0', 10);
    const newHours = h <= 0 ? '23' : String(h - 1).padStart(2, '0');
    setHours(newHours);
    updateTime(newHours, minutes || '00');
  };
  
  const incrementMinutes = () => {
    const m = parseInt(minutes || '0', 10);
    const newMinutes = m >= 59 ? '00' : String(m + 1).padStart(2, '0');
    setMinutes(newMinutes);
    updateTime(hours || '00', newMinutes);
  };
  
  const decrementMinutes = () => {
    const m = parseInt(minutes || '0', 10);
    const newMinutes = m <= 0 ? '59' : String(m - 1).padStart(2, '0');
    setMinutes(newMinutes);
    updateTime(hours || '00', newMinutes);
  };
  
  const handleKeyDown = (e, field) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      field === 'hours' ? incrementHours() : incrementMinutes();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      field === 'hours' ? decrementHours() : decrementMinutes();
    }
  };
  
  const handleBlur = (field) => {
    if (field === 'hours') {
      const paddedHours = hours.padStart(2, '0');
      if (hours.length === 1) {
        setHours(paddedHours);
      }
      updateTime(paddedHours, minutes);
    } else if (field === 'minutes') {
      const paddedMinutes = minutes.padStart(2, '0');
      if (minutes.length === 1) {
        setMinutes(paddedMinutes);
      }
      updateTime(hours, paddedMinutes);
    }
    setFocusedField(null);
  };
  
  return (
    <div className={cn("flex items-center justify-between h-12 bg-white border rounded-md px-3 flex-1", className, disabled && "opacity-50 cursor-not-allowed")} {...props}>
      <div className="flex items-center space-x-1">
        <input
          id="time-hours"
          type="text"
          value={hours}
          onChange={handleHoursChange}
          onKeyDown={(e) => handleKeyDown(e, 'hours')}
          onFocus={() => setFocusedField('hours')}
          onBlur={() => handleBlur('hours')}
          placeholder="00"
          className="w-8 text-center outline-none bg-transparent"
          maxLength="2"
          disabled={disabled}
        />
        <span className="text-gray-500">:</span>
        <input
          id="time-minutes"
          type="text"
          value={minutes}
          onChange={handleMinutesChange}
          onKeyDown={(e) => handleKeyDown(e, 'minutes')}
          onFocus={() => setFocusedField('minutes')}
          onBlur={() => handleBlur('minutes')}
          placeholder="00"
          className="w-8 text-center outline-none bg-transparent"
          maxLength="2"
          disabled={disabled}
        />
      </div>
      
      {!disabled && (
        <div className="flex flex-col ml-2 -mr-1">
          <button
            type="button"
            onClick={focusedField === 'hours' ? incrementHours : incrementMinutes}
            className="text-gray-400 hover:text-gray-600 p-0 h-5 w-5 flex items-center justify-center"
            tabIndex={-1}
          >
            <svg width="10" height="6" viewBox="0 0 10 6" fill="currentColor">
              <path d="M5 0L10 6H0L5 0Z" />
            </svg>
          </button>
          <button
            type="button"
            onClick={focusedField === 'hours' ? decrementHours : decrementMinutes}
            className="text-gray-400 hover:text-gray-600 p-0 h-5 w-5 flex items-center justify-center"
            tabIndex={-1}
          >
            <svg width="10" height="6" viewBox="0 0 10 6" fill="currentColor">
              <path d="M5 6L0 0H10L5 6Z" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
};

// Convert date and time from Warsaw timezone to UTC for server
const convertWarsawToUTC = (date, timeString) => {
  if (!date || !timeString) return null;
  
  const [hours, minutes] = timeString.split(':').map(Number);
  
  // Get the date parts as numbers
  const year = date.getFullYear();
  const month = date.getMonth() + 1; // getMonth() returns 0-11, we need 1-12
  const day = date.getDate();
  
  // Create a temporary date assuming it's in Warsaw timezone
  // We'll use the inverse operation of what toLocaleString does
  
  // Create a fake UTC date with our Warsaw date/time values
  const fakeUTC = new Date(Date.UTC(year, month - 1, day, hours, minutes, 0));
  
  // See what this UTC date looks like when displayed in Warsaw timezone
  const warsawDisplay = fakeUTC.toLocaleString('sv-SE', {
    timeZone: WARSAW_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
  
  // Parse what Warsaw timezone shows
  const [warsawDatePart, warsawTimePart] = warsawDisplay.split(' ');
  const [wYear, wMonth, wDay] = warsawDatePart.split('-').map(Number);
  const [wHour, wMinute] = warsawTimePart.split(':').map(Number);
  
  // Calculate the difference
  const yearDiff = year - wYear;
  const monthDiff = month - wMonth;
  const dayDiff = day - wDay;
  const hourDiff = hours - wHour;
  const minuteDiff = minutes - wMinute;
  
  // Apply the correction
  return new Date(Date.UTC(
    year + yearDiff,
    (month - 1) + monthDiff,
    day + dayDiff,
    hours + hourDiff,
    minutes + minuteDiff,
    0
  ));
};

// Convert UTC date from server to Warsaw timezone parts for display
const convertUTCToWarsaw = (utcDate) => {
  if (!utcDate) return { date: new Date(), time: '09:00' };
  
  const date = new Date(utcDate);
  
  // Use the reliable sv-SE locale format (YYYY-MM-DD HH:mm:ss) in Warsaw timezone
  const warsawString = date.toLocaleString('sv-SE', {
    timeZone: WARSAW_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });
  
  // Parse the result (format: "2024-01-15 14:30:00")
  const [datePart, timePart] = warsawString.split(' ');
  const [year, month, day] = datePart.split('-');
  const [hour, minute] = timePart.split(':');
  
  return {
    // Create date using UTC constructor to avoid timezone interpretation issues
    // The date picker will treat this as a local date, which is what we want
    date: new Date(Date.UTC(parseInt(year), parseInt(month) - 1, parseInt(day), 12, 0, 0)),
    time: `${hour}:${minute}`
  };
};

export function TaskDateTimePicker({ 
  datetime, 
  setDatetime, 
  label, 
  error = false, 
  errorMessage = null, 
  inputClassName, 
  data_testid = null, 
  disabled = false,
  ...props 
}) {
  const readOnly = typeof disabled === "boolean" && disabled;
  
  // Track initialization and prevent loops using useRef
  const hasInitialized = React.useRef(!!datetime); // Already initialized if datetime prop exists
  
  // Internal state for date and time - initialize from prop if available
  const [selectedDate, setSelectedDate] = React.useState(() => {
    if (datetime) {
      const { date } = convertUTCToWarsaw(datetime);
      return date || new Date();
    }
    // Default to end of current day for new tasks
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  });
  
  const [selectedTime, setSelectedTime] = React.useState(() => {
    if (datetime) {
      const { time } = convertUTCToWarsaw(datetime);
      return time || '23:59';
    }
    return '23:59'; // Default to end of day for new tasks
  });
  
  // For new tasks, immediately initialize the parent with a default datetime
  React.useEffect(() => {
    if (!datetime && selectedDate && selectedTime && !hasInitialized.current) {
      const utcDateTime = convertWarsawToUTC(selectedDate, selectedTime);
      if (utcDateTime && !isNaN(utcDateTime.getTime())) {
        setDatetime(utcDateTime);
        hasInitialized.current = true;
      }
    }
  }, []); // Run only once on mount
  const lastDatetimeTimestamp = React.useRef(datetime ? datetime.getTime() : null);
  const conversionAttempts = React.useRef(0);
  const maxConversionAttempts = 3; // Prevent infinite conversion loops
  
  // Initialize from datetime prop (run only when datetime actually changes)
  React.useEffect(() => {
    // Compare timestamps instead of object references
    const currentTimestamp = datetime ? datetime.getTime() : null;
    const isDifferent = currentTimestamp !== lastDatetimeTimestamp.current;
    
    // Reset conversion attempts if datetime changed significantly (more than 1 minute)
    if (isDifferent && lastDatetimeTimestamp.current && currentTimestamp) {
      const timeDiff = Math.abs(currentTimestamp - lastDatetimeTimestamp.current);
      if (timeDiff > 60000) { // 1 minute
        conversionAttempts.current = 0;
      }
    }
    
    // Only run if datetime timestamp is different and we haven't exceeded conversion attempts
    if (datetime && isDifferent && conversionAttempts.current < maxConversionAttempts) {
      lastDatetimeTimestamp.current = currentTimestamp;
      conversionAttempts.current += 1;
      
      const { date, time } = convertUTCToWarsaw(datetime);
      
      if (date && time) {
        setSelectedDate(date);
        setSelectedTime(time);
        hasInitialized.current = true;
      }
    }
  }, [datetime]); // Only depend on datetime, not state variables
  
  // Update parent when date or time changes (only after initialization)
  React.useEffect(() => {
    // Only update parent if we've initialized and user is actually changing values
    if (selectedDate && selectedTime && hasInitialized.current && conversionAttempts.current < maxConversionAttempts) {
      const utcDateTime = convertWarsawToUTC(selectedDate, selectedTime);
      if (utcDateTime && !isNaN(utcDateTime.getTime())) {
        // Check if the new UTC time is significantly different to avoid conversion loops
        const timeDiff = Math.abs((utcDateTime.getTime() - (datetime?.getTime() || 0)));
        
        // For new tasks (no existing datetime), always update
        // For existing tasks, only skip if difference is very small (less than 1 minute) to avoid loops
        const shouldUpdate = !datetime || timeDiff > 60000; // 1 minute threshold
        
        if (shouldUpdate) {
          setDatetime(utcDateTime);
        }
      }
    }
  }, [selectedDate, selectedTime]);
  
  // Time change is now handled by CustomTimeInput component directly
  
  const handleDateChange = (newDate) => {
    // Reset conversion attempts when user manually changes date
    conversionAttempts.current = 0;
    setSelectedDate(newDate);
  };
  
  const formatDisplayDateTime = () => {
    if (!selectedDate || !selectedTime) return "";
    return `${format(selectedDate, "dd/MM/yyyy")} ${selectedTime} (Warsaw, 24h)`;
  };

  return (
    <div>
      <Label htmlFor="inputId" className="pb-1 text-sm font-medium">{label}</Label>
      
      <div className="flex gap-2">
        {/* Date Picker */}
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant={"outline"}
              className={cn(
                `flex-1 justify-start text-left font-normal ${inputClassName}`,
                !selectedDate && "text-muted-foreground",
                readOnly && "bg-inherit focus:outline-none focus:shadow-none hover:bg-white hover:cursor-default"
              )}
              data-testid={data_testid ? `${data_testid}_date` : null}
              style={readOnly ? { cursor: 'default', pointerEvents: 'none' } : {}}
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {selectedDate ? format(selectedDate, "dd/MM/yyyy") : <span>Select date</span>}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            {!readOnly && (
              <Calendar
                mode="single"
                selected={selectedDate}
                fromYear={1940}
                toYear={2040}
                onSelect={handleDateChange}
                captionLayout="dropdown-buttons"
                initialFocus
                data-testid={data_testid ? `${data_testid}_calendar` : null}
                weekStartsOn={1}
                disabled={disabled}
                {...props}
              />
            )}
          </PopoverContent>
        </Popover>

        {/* Custom 24-Hour Time Picker */}
        <div className="flex-1">
          <div className="flex items-center space-x-2">
            <ClockIcon className="h-4 w-4 text-gray-500" />
            <CustomTimeInput
              value={selectedTime}
              onChange={(newTime) => {
                conversionAttempts.current = 0;
                setSelectedTime(newTime);
              }}
              className={inputClassName}
              disabled={readOnly}
              data-testid={data_testid ? `${data_testid}_time` : null}
            />
          </div>
          <div className="text-xs text-gray-500 mt-1">24h format</div>
        </div>
      </div>

      {/* Display combined date-time with timezone info */}
      <div className="mt-1 text-xs text-gray-600">
        {formatDisplayDateTime()}
      </div>

      {/* Error display */}
      {error && (
        <div className="mt-2">
          <Alert variant="destructive" className="relative mb-2 h-12 flex items-center justify-start !pl-[0.75rem]">
            <AlertCircle className="h-4 w-4 !static !mr-2" />
            <AlertTitle className="m-0 !p-0">
              {errorMessage}
            </AlertTitle>
          </Alert>
        </div>
      )}
    </div>
  )
}